import { getAudienceIntegrations, INTEGRATION_FIELDS } from '../collections'

/**
 * Bulk edits are described as declarative "operations" instead of editing each
 * audience individually. The same set of operations is applied to every loaded
 * audience, gated by the collection's capabilities, and the per-audience diff
 * is computed from it. Empty / 'none' operations leave the field untouched.
 */

export const NAME_MODES = {
  NONE: 'none',
  FIND_REPLACE: 'find_replace',
  ADD_PREFIX: 'add_prefix',
  SET_SEGMENT: 'set_segment',
  ADD_SEGMENT: 'add_segment',
}

// sentinel `before_segment_index` meaning "append after the last segment",
// as opposed to a numeric 1-based index meaning "insert before that segment"
export const APPEND_SEGMENT = 'end'

export const LIST_MODES = {
  NONE: 'none',
  ADD: 'add',
  REMOVE: 'remove',
  REPLACE: 'replace',
}

export const createEmptyOperations = () => ({
  name: {
    mode: NAME_MODES.NONE,
    find: '',
    replace: '',
    prefix: '',
    segment_index: 1,
    value: '',
    before_segment_index: 1,
    new_segment: '',
  },
  status: { value: '' }, // empty value means "do not touch"
  regions: { mode: LIST_MODES.NONE, values: [] },
  destinations: { mode: LIST_MODES.NONE, values: [] },
  // { [camelCaseField]: value } — empty value means "do not touch"
  fields: {},
})

/**
 * Operations are persisted per session (localStorage) and restored verbatim, so
 * a session created before a new operation field existed would come back missing
 * that key. Deep-merge over a fresh template so every field is always present,
 * regardless of how old the saved shape is.
 */
export const normalizeOperations = (operations) => {
  const empty = createEmptyOperations()
  if (!operations) return empty
  return {
    ...empty,
    ...operations,
    name: { ...empty.name, ...operations.name },
    status: { ...empty.status, ...operations.status },
    regions: { ...empty.regions, ...operations.regions },
    destinations: { ...empty.destinations, ...operations.destinations },
    fields: { ...empty.fields, ...operations.fields },
  }
}

export const splitName = (name = '') =>
  name
    .split('>')
    .map((segment) => segment.trim())
    .filter(Boolean)

const joinName = (segments) => segments.filter(Boolean).join(' > ')

// react-select decorates selected options; strip its artifacts before saving
const cleanOption = (option) =>
  Object.fromEntries(Object.entries(option).filter(([key]) => !['label', 'value'].includes(key)))

const listIds = (list = []) =>
  list
    .map(({ _id }) => _id)
    .sort()
    .join(',')

const sameList = (a = [], b = []) => listIds(a) === listIds(b)

const applyNameOperation = (name, operation) => {
  if (operation.mode === NAME_MODES.FIND_REPLACE && operation.find) {
    return joinName(splitName(name.split(operation.find).join(operation.replace)))
  }

  if (operation.mode === NAME_MODES.ADD_PREFIX && operation.prefix.trim()) {
    // apply the untrimmed prefix (so a trailing space is kept intentionally),
    // but normalise separator spacing like the other modes do
    return joinName(splitName(`${operation.prefix}${name}`))
  }

  if (operation.mode === NAME_MODES.SET_SEGMENT) {
    const segments = splitName(name)
    const index = operation.segment_index - 1
    if (index < 0 || index >= segments.length) return name
    segments[index] = operation.value.trim()
    return joinName(segments)
  }

  if (operation.mode === NAME_MODES.ADD_SEGMENT && operation.new_segment.trim()) {
    const segments = splitName(name)
    // "At the end" appends to every audience's own end, regardless of depth
    if (operation.before_segment_index === APPEND_SEGMENT) {
      segments.push(operation.new_segment.trim())
      return joinName(segments)
    }
    const index = operation.before_segment_index - 1
    // only insert when the "before" segment exists, mirroring SET_SEGMENT
    if (index < 0 || index >= segments.length) return name
    segments.splice(index, 0, operation.new_segment.trim())
    return joinName(segments)
  }

  return name
}

const applyListOperation = (current = [], operation) => {
  const values = operation.values.map(cleanOption)

  switch (operation.mode) {
    case LIST_MODES.ADD: {
      const additions = values.filter((value) => !current.some(({ _id }) => _id === value._id))
      return [...current, ...additions]
    }
    case LIST_MODES.REMOVE:
      return current.filter(({ _id }) => !values.some((value) => value._id === _id))
    case LIST_MODES.REPLACE:
      return values
    default:
      return current
  }
}

/**
 * Computes the changes the operations produce on a single audience.
 * Returns { [field]: { from, to } } containing only the fields that change.
 */
export function buildChanges(audience, operations, collection) {
  const { capabilities } = collection
  const changes = {}

  const newName = applyNameOperation(audience.name, operations.name)
  if (newName !== audience.name) {
    changes.name = { from: audience.name, to: newName }
  }

  if (
    capabilities.status &&
    operations.status.value &&
    audience.status !== operations.status.value
  ) {
    changes.status = { from: audience.status, to: operations.status.value }
  }

  let newDestinations = audience.destinations
  if (capabilities.regions && operations.regions.mode !== LIST_MODES.NONE) {
    const newRegions = applyListOperation(audience.regions, operations.regions)
    if (!sameList(audience.regions, newRegions)) {
      changes.regions = { from: audience.regions || [], to: newRegions }
    }
  }

  if (capabilities.destinations && operations.destinations.mode !== LIST_MODES.NONE) {
    newDestinations = applyListOperation(audience.destinations, operations.destinations)
    if (!sameList(audience.destinations, newDestinations)) {
      changes.destinations = { from: audience.destinations || [], to: newDestinations }
    }
  }

  if (capabilities.integrationFields) {
    // destination-specific fields only apply to audiences delivering to that
    // integration (based on the destinations the audience will have after this edit)
    const integrations = getAudienceIntegrations({ destinations: newDestinations })

    for (const [field, value] of Object.entries(operations.fields)) {
      if (value === '' || value === null || value === undefined) continue

      const fieldApplies = Object.entries(INTEGRATION_FIELDS).some(
        ([integration, fields]) => integrations.includes(integration) && fields.includes(field)
      )
      if (!fieldApplies) continue

      if (String(audience[field] ?? '') !== String(value)) {
        changes[field] = { from: audience[field], to: value }
      }
    }
  }

  return changes
}

export const buildPlan = (audiences, operations, collection) =>
  audiences.map((audience) => {
    const changes = buildChanges(audience, operations, collection)
    return { audience, changes, changeCount: Object.keys(changes).length }
  })

export const changesToBody = (changes) =>
  Object.fromEntries(Object.entries(changes).map(([field, { to }]) => [field, to]))

export const formatChangeValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (Array.isArray(value)) return value.map(({ name }) => name).join(', ') || '—'
  return String(value)
}
